import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

const createStyles = (themeColors = {}) => {
  const primary = themeColors.primary?.main || themeColors.primary || "#0e0e0fff";
  const textPrimary = themeColors.text?.primary || "#111111";
  const textSecondary = themeColors.text?.secondary || "#666666";
  const divider = themeColors.divider || "#e0e0e0";
  const surface = themeColors.background?.paper || themeColors.background?.default || "#f5f5f5";
  const altSurface = themeColors.background?.alt || "#fafafa";
  const success = themeColors.success?.main || "#4caf50";
  const error = themeColors.error?.main || "#f44336";

  return StyleSheet.create({
    page: {
      padding: 30,
      fontSize: 10,
      fontFamily: "Helvetica",
      color: textPrimary,
      backgroundColor: "#FFFFFF",
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 15,
    },
    logoSection: {
      flexDirection: "column",
    },
    logo: {
      width: 40,
      height: 40,
      marginBottom: 10,
    },
    invoiceTitle: {
      fontSize: 28,
      fontWeight: "bold",
      color: primary,
    },
    dateSection: {
      alignItems: "flex-end",
    },
    dateLabel: {
      fontSize: 9,
      color: textSecondary,
    },
    dateValue: {
      fontSize: 14,
      fontWeight: "bold",
      marginTop: 2,
    },
    divider: {
      borderBottom: 1,
      borderColor: divider,
      marginVertical: 10,
    },
    addressSection: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    addressBlock: {
      flex: 1,
    },
    addressLabel: {
      fontSize: 9,
      color: textSecondary,
      marginBottom: 5,
      textTransform: "uppercase",
    },
    companyName: {
      fontSize: 14,
      fontWeight: "bold",
      marginBottom: 3,
      color: textPrimary,
    },
    addressText: {
      fontSize: 10,
      color: textPrimary,
      marginBottom: 2,
    },
    email: {
      fontSize: 10,
      color: textSecondary,
    },
    periodSection: {
      marginBottom: 10,
    },
    periodLabel: {
      fontSize: 9,
      color: textSecondary,
      marginBottom: 3,
    },
    periodValue: {
      fontSize: 11,
      fontWeight: "bold",
    },
    table: {
      marginTop: 5,
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: surface,
      padding: 8,
      fontWeight: "bold",
      borderRadius: 4,
    },
    tableRow: {
      flexDirection: "row",
      padding: 4,
      borderBottom: 1,
      borderColor: divider,
    },
    tableRowAlt: {
      flexDirection: "row",
      padding: 4,
      borderBottom: 1,
      borderColor: divider,
      backgroundColor: altSurface,
    },
    tableRowRejected: {
      flexDirection: "row",
      padding: 4,
      borderBottom: 1,
      borderColor: divider,
      backgroundColor: "#fff3e0",
    },
    // Column widths without Time column
    col1: { width: "14%", fontSize: 8 }, // Transaction ID
    col2: { width: "12%", fontSize: 8 }, // User ID
    col3: { width: "12%", fontSize: 8 }, // Date
    col4: { width: "11%", fontSize: 8 }, // Amount
    col5: { width: "11%", fontSize: 8 }, // Platform Fee
    col6: { width: "11%", fontSize: 8 }, // Transaction Fee
    col7: { width: "11%", fontSize: 8 }, // Penalty
    col8: { width: "12%", fontSize: 8 }, // Net Earning
    statusCol: { width: "10%", fontSize: 8, textAlign: "right" }, // Status

    statusCompleted: { color: success, fontWeight: "bold" },
    statusRejected: { color: error, fontWeight: "bold" },
    penaltyText: { color: error, fontWeight: "bold" },
    feeText: { color: textSecondary },
    netEarningGray: { color: textSecondary },

    // Inline summary row styles
    summaryInlineRow: {
      flexDirection: "row",
      marginTop: 8,
      paddingVertical: 6,
      paddingHorizontal: 8,
      borderTop: 2,
      borderColor: "#000",
      backgroundColor: surface,
    },
    summaryInlineLabel: {
      fontSize: 9,
      fontWeight: "bold",
    },
    summaryInlineText: {
      fontSize: 8,
    },
    summaryInlineStrong: {
      fontSize: 8,
      fontWeight: "bold",
    },

    footer: {
      marginTop: 15,
      padding: 12,
      backgroundColor: surface,
      borderRadius: 4
    },
    footerLabel: {
      fontSize: 9,
      fontWeight: "bold",
      marginBottom: 3
    },
    footerText: {
      fontSize: 9,
      color: textSecondary
    },
    pageNumber: {
      position: "absolute",
      bottom: 30,
      right: 40,
      fontSize: 9,
      color: textSecondary
    },
    boldText: {
      fontWeight: "bold"
    },
  });
};

const TransactionInvoicePDF = ({
  transactions,
  vendorData,
  logoSrc,
  selectedDate,
  themeColors = {},
  isPreview = false
}) => {
  const darkPalette = {
    background: { default: "#0b0b0b", paper: "#121212", alt: "#0f0f0f" },
    text: { primary: "#e6eef8", secondary: "#bfc9d6" },
    divider: "#262626",
    primary: themeColors.primary || "#90caf9",
    success: { main: themeColors.success?.main || "#66bb6a" },
    error: { main: themeColors.error?.main || "#ef5350" },
  };

  const effectiveTheme = isPreview
    ? { ...themeColors, ...darkPalette }
    : themeColors;

  const styles = createStyles(effectiveTheme);
  const invoiceDate = new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  // Calculate detailed summary stats
  const totalTransactions = transactions.length;
  const completedTransactions = transactions.filter(
    (txn) => txn.status === "COMPLETED"
  ).length;
  const rejectedTransactions = transactions.filter(
    (txn) => txn.status === "REJECTED"
  ).length;

  const totalAmount = transactions.reduce((sum, txn) => sum + (txn.amount || 0), 0);
  const totalPlatformFee = transactions.reduce((sum, txn) => sum + (txn.platformFee || 0), 0);
  const totalTransactionFee = transactions.reduce((sum, txn) => sum + (txn.transactionFee || 0), 0);
  const totalPenalties = transactions.reduce((sum, txn) => sum + (txn.rejectionPenalty || 0), 0);

  // ✅ Correct calculation: Amount - Platform Fee - Transaction Fee - Penalties
  const totalNetEarnings = totalAmount - totalPlatformFee - totalTransactionFee - totalPenalties
  // Use Rs instead of rupee symbol
  const formatCurrencyWithCommas = (amount) => {
    const formatted = amount.toFixed(2);
    const [integer, decimal] = formatted.split('.');
    const withCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `Rs ${withCommas}.${decimal}`;
  };
  const transactionPeriod = selectedDate || "All Transactions";

  const superAdmin = {
    name: "Zerox Cloud",
    email: "admin@zeroxcloud.com",
    phone: "+91 9876543210",
    address: "123 Business Park, Pune, Maharashtra 411001",
  };

  // OPTIMIZED PAGINATION
  const rowsPerFirstPage = 12;
  const rowsPerSubsequentPage = 27;

  const pages = [];
  let remaining = [...transactions];

  pages.push(remaining.slice(0, rowsPerFirstPage));
  remaining = remaining.slice(rowsPerFirstPage);

  while (remaining.length > 0) {
    pages.push(remaining.slice(0, rowsPerSubsequentPage));
    remaining = remaining.slice(rowsPerSubsequentPage);
  }

  return (
    <Document>
      {pages.map((pageTransactions, pageIndex) => {
        const isFirstPage = pageIndex === 0;
        const isLastPage = pageIndex === pages.length - 1;

        return (
          <Page key={pageIndex} size="A4" orientation="landscape" style={styles.page}>
            {/* Header - Only on first page */}
            {isFirstPage && (
              <>
                <View style={styles.header}>
                  <View style={styles.logoSection}>
                    <Image src={logoSrc} style={styles.logo} />
                    <Text style={styles.invoiceTitle}>INVOICE</Text>
                  </View>
                  <View style={styles.dateSection}>
                    <Text style={styles.dateLabel}>Date:</Text>
                    <Text style={styles.dateValue}>{invoiceDate}</Text>
                  </View>
                </View>

                <View style={styles.divider} />

                {/* From/To Section */}
                <View style={styles.addressSection}>
                  <View style={styles.addressBlock}>
                    <Text style={styles.addressLabel}>From:</Text>
                    <Text style={styles.companyName}>{superAdmin.name}</Text>
                    <Text style={styles.addressText}>{superAdmin.address}</Text>
                    <Text style={styles.email}>{superAdmin.email}</Text>
                    <Text style={styles.email}>{superAdmin.phone}</Text>
                  </View>

                  <View style={[styles.addressBlock, { alignItems: "flex-end" }]}>
                    <Text style={styles.addressLabel}>Billed to:</Text>
                    <Text style={styles.companyName}>{vendorData?.name || "Vendor Name"}</Text>
                    <Text style={styles.email}>{vendorData?.email || "vendor@example.com"}</Text>
                    <Text style={styles.email}>{vendorData?.phoneNumber || "+91 1234567890"}</Text>
                  </View>
                </View>

                {/* Period */}
                <View style={styles.periodSection}>
                  <Text style={styles.periodLabel}>Period:</Text>
                  <Text style={styles.periodValue}>{transactionPeriod}</Text>
                </View>

                <View style={styles.divider} />
              </>
            )}

            {/* Table Header - Without Time column */}
            <View style={styles.tableHeader}>
              <Text style={styles.col1}>Transaction ID</Text>
              <Text style={styles.col2}>User ID</Text>
              <Text style={styles.col3}>Date</Text>
              <Text style={styles.col4}>Amount</Text>
              <Text style={styles.col5}>Platform Fee</Text>
              <Text style={styles.col6}>Trans. Fee</Text>
              <Text style={styles.col7}>Penalty</Text>
              <Text style={styles.col8}>Net Earning</Text>
              <Text style={styles.statusCol}>Status</Text>
            </View>

            {/* Table Rows */}
            <View style={styles.table}>
              {pageTransactions.map((txn, index) => {
                const isRejected = txn.status === "REJECTED";
                const rowStyle = isRejected
                  ? styles.tableRowRejected
                  : (index % 2 === 0 ? styles.tableRow : styles.tableRowAlt);

                return (
                  <View key={`${pageIndex}-${index}`} style={rowStyle}>
                    <Text style={styles.col1}>{txn.transactionId}</Text>
                    <Text style={styles.col2}>{txn.userId}</Text>
                    <Text style={styles.col3}>{txn.date}</Text>
                    <Text style={styles.col4}>{formatCurrencyWithCommas(txn.amount || 0)}</Text>
                    <Text style={[styles.col5, styles.feeText]}>
                      -{formatCurrencyWithCommas(txn.platformFee || 0)}
                    </Text>
                    <Text style={[styles.col6, styles.feeText]}>
                      -{formatCurrencyWithCommas(txn.transactionFee || 0)}
                    </Text>
                    <Text style={[styles.col7, styles.penaltyText]}>
                      {txn.rejectionPenalty > 0 ? `-${formatCurrencyWithCommas(txn.rejectionPenalty)}` : ""}
                    </Text>
                    <Text style={[styles.col8, isRejected && styles.netEarningGray]}>
                      {formatCurrencyWithCommas(txn.netEarning || 0)}
                    </Text>

                    <Text
                      style={[
                        styles.statusCol,
                        txn.status === "COMPLETED"
                          ? styles.statusCompleted
                          : styles.statusRejected,
                      ]}
                    >
                      {txn.status}
                    </Text>
                  </View>
                );
              })}
            </View>

            {isLastPage && (
              <>
                {/* First row: Column totals */}
                <View style={styles.summaryInlineRow}>
                  {/* ✅ Show transaction count under Transaction ID */}
                  <Text style={[styles.col1, styles.summaryInlineStrong]}>
                    {totalTransactions}
                  </Text>

                  {/* ✅ Blank under User ID */}
                  <Text style={styles.col2}></Text>

                  {/* ✅ Blank under Date */}
                  <Text style={styles.col3}></Text>

                  {/* Under Amount – Total Amount */}
                  <Text style={[styles.col4, styles.summaryInlineStrong]}>
                    {formatCurrencyWithCommas(totalAmount)}
                  </Text>

                  <Text style={[styles.col5, styles.summaryInlineStrong, styles.feeText]}>
                    -{formatCurrencyWithCommas(totalPlatformFee)}
                  </Text>

                  <Text style={[styles.col6, styles.summaryInlineStrong, styles.feeText]}>
                    -{formatCurrencyWithCommas(totalTransactionFee)}
                  </Text>

                  <Text style={[styles.col7, styles.summaryInlineStrong, styles.penaltyText]}>
                    {totalPenalties > 0 ? `-${formatCurrencyWithCommas(totalPenalties)}` : ""}
                  </Text>

                  <Text style={[styles.col8, styles.summaryInlineStrong, { color: "#4caf50" }]}>
                    {formatCurrencyWithCommas(totalNetEarnings)}
                  </Text>


                  {/* Empty under Status */}
                  <Text style={styles.statusCol}></Text>
                </View>


                {/* Footer */}
                <View style={styles.footer}>
                  <Text style={styles.footerLabel}>Note:</Text>
                  <Text style={styles.footerText}>
                    Thank you for choosing Zerox Cloud! This invoice covers all completed transactions.
                    {rejectedTransactions > 0 && ` Penalties applied for ${rejectedTransactions} rejected transaction(s).`}
                    {" "}Payment terms: Net 30 days. For queries, contact {superAdmin.email}.
                  </Text>
                </View>
              </>
            )}

            {/* Page Number */}
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
              fixed
            />
          </Page>
        );
      })}
    </Document>
  );
};

export default TransactionInvoicePDF;
